import { ILegalTransactionRelationship } from '../../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { ILegalTransactionRelationshipMember } from '../../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipMember';
import { LegalTransactionRelationshipRoleEnum } from '../../../../../../Interfaces/ILegalTransactionRelationship/LegalTransactionRelationshipRoleEnum';
import { IFamilyRelationship } from '../../../../../../Interfaces/ILegalTransactionRelationship/Relationships/Family/ILegalTransactionFamilyRelationship';
import {
  legalClientStore,
  LegalClientStore,
} from '../../../generic/legalClientStore';
import {
  isFamilyRelationship,
  isMarriageRelationship,
} from '../../../Inherit/utilities';
import { Id, Tree, TreeNodeKind, TreeNodeType } from './types';

export function pid(id: string | number): string {
  return String(id);
}

export function mid(id: string | number): string {
  return `m-${id}`;
}

export function dim(mid: string): string {
  return mid.startsWith('m-') ? mid.slice(2) : mid;
}

function getPersonNodeName(
  getLegalClient: LegalClientStore['getLegalClient'],
  id: Id
) {
  const lc = getLegalClient(Number(id));
  return `${lc?.FirstName ?? ''} ${lc?.LastName ?? ''}`.trim() + ` (${id})`;
}

function ensurePersonNode(tree: Tree, id: Id): TreeNodeType {
  const getLegalClient = legalClientStore.getState().getLegalClient;

  if (!tree[id]) {
    tree[id] = {
      id: id,
      name: getPersonNodeName(getLegalClient, id),
      kind: TreeNodeKind.Person,
    };
  }
  return tree[id];
}

function initializeAllPersons(
  tree: Tree,
  relationships: ILegalTransactionRelationship[]
) {
  for (const rel of relationships) {
    for (const m of rel.Members) {
      ensurePersonNode(tree, pid(m.idLegalClient));
    }
  }
}

function getMarriageRelationshipNodeKind(
  rel: ILegalTransactionRelationship
): TreeNodeKind {
  if (isMarriageRelationship(rel)) {
    return rel.data.divorceDate
      ? TreeNodeKind.Divorce // divorced marriage
      : TreeNodeKind.Marriage; // current marriage
  }

  throw new Error(
    `Must not call getMarriageRelationshipNodeKind on non-marriage relationship (id: ${rel.idRelationship}, type: ${rel.idRelationshipType})`
  );
}

function ensureMarriageNode(tree: Tree, id: Id, memberIds: Id[]): TreeNodeType {
  if (!tree[id]) {
    tree[id] = {
      id: id,
      name: `💍`,
      kind: TreeNodeKind.Marriage,
    };
  }
  return tree[id];
}

function ensureDivorceNode(tree: Tree, id: Id, memberIds: Id[]): TreeNodeType {
  if (!tree[id]) {
    tree[id] = {
      id: id,
      name: `💔`,
      kind: TreeNodeKind.Divorce,
    };
  }
  return tree[id];
}

function assignMarriages(
  tree: Tree,
  relationships: ILegalTransactionRelationship[]
) {
  const marriageRelationships = relationships
    .filter(isMarriageRelationship)
    .filter(r => r.Members.length === 2);

  for (const rel of marriageRelationships) {
    const memberIds = rel.Members.map(m => pid(m.idLegalClient));
    const [id1, id2] = memberIds;

    const person1 = ensurePersonNode(tree, id1);
    const person2 = ensurePersonNode(tree, id2);

    const marriageNodeId = mid(rel.idRelationship);
    const kind = getMarriageRelationshipNodeKind(rel);

    if (kind === TreeNodeKind.Marriage) {
      const marriageNode = ensureMarriageNode(tree, marriageNodeId, memberIds);
      marriageNode.parents = [id1, id2];
      person1.spouses = person1.spouses || [];
      person1.spouses.push(pid(person2.id));
      person2.isSpouse = true;
    }

    if (kind === TreeNodeKind.Divorce) {
      const divorceNode = ensureDivorceNode(tree, marriageNodeId, memberIds);
      divorceNode.parents = [id1, id2];
      person1.exSpouses = person2.exSpouses || [];
      person1.exSpouses.push(pid(person2.id));
      person2.isExSpouse = true;
    }

    person1.children = person1.children || [];
    person1.children.push(marriageNodeId);
    person2.children = person2.children || [];
    person2.children.push(marriageNodeId);
  }
}

function setIntersection<T>(sets: Set<T>[]): Set<T> {
  if (sets.length === 0) return new Set<T>();

  const intersection = new Set<T>(sets[0]);
  for (const s of sets.slice(1)) {
    for (const item of intersection) {
      if (!s.has(item)) {
        intersection.delete(item);
      }
    }
  }

  return intersection;
}

function ensureFamilyNode(tree: Tree, id: Id): TreeNodeType {
  if (!tree[id]) {
    tree[id] = {
      id: id,
      name: `👪`,
      kind: TreeNodeKind.Family,
    };
  }
  return tree[id];
}

function getParentsFromRelationship(rel: IFamilyRelationship) {
  return rel.Members.filter(
    m => m.idRelationshipRole === LegalTransactionRelationshipRoleEnum.Parent
  );
}

function getChildrenFromRelationship(rel: IFamilyRelationship) {
  return rel.Members.filter(
    m => m.idRelationshipRole === LegalTransactionRelationshipRoleEnum.Child
  );
}

function getMemberNodeId(member: ILegalTransactionRelationshipMember): Id {
  return pid(member.idLegalClient);
}

function provide<T, U extends unknown[], R>(
  fn: (arg1: T, ...args: U) => R,
  arg1: T
): (...args: U) => R {
  return (...args: U) => fn(arg1, ...args);
}

export function assignFamilies(
  tree: Tree,
  relationships: ILegalTransactionRelationship[]
) {
  const ensurePerson = provide(ensurePersonNode, tree);

  const familyRelationships = relationships.filter(isFamilyRelationship);

  for (const rel of familyRelationships) {
    const parentNodeIds = getParentsFromRelationship(rel).map(getMemberNodeId);
    const childNodeIds = getChildrenFromRelationship(rel).map(getMemberNodeId);

    const parentNodes = parentNodeIds.map(ensurePerson);
    const childNodes = childNodeIds.map(ensurePerson);

    const parentConnectorIds = parentNodes.map(n => new Set(n.children));
    const relationshipsSharedByAllParents = Array.from(
      setIntersection(parentConnectorIds)
    );

    if (
      parentNodeIds.length >= 2 &&
      relationshipsSharedByAllParents.length > 0
    ) {
      for (const id of relationshipsSharedByAllParents) {
        const connectorNode = tree[id];
        connectorNode.children = connectorNode.children || [];
        connectorNode.children.push(...childNodes.map(c => c.id));

        childNodes.forEach(cNode => {
          cNode.parents = cNode.parents || [];
          cNode.parents.push(connectorNode.id);
        });
      }
    } else {
      const familyNodeId = mid(rel.idRelationship);

      const familyNode = ensureFamilyNode(tree, familyNodeId);
      familyNode.parents = parentNodeIds;
      familyNode.children = childNodeIds;

      for (const pNode of parentNodes) {
        pNode.children = pNode.children || [];
        pNode.children.push(familyNodeId);
      }

      for (const cNode of childNodes) {
        cNode.parents = cNode.parents || [];
        cNode.parents.push(familyNodeId);
      }
    }
  }
}

function markPrimaryLineage(tree: Tree, id: Id) {
  const node = tree[id];
  if (!node) {
    return;
  }

  node.isPrimaryLineage = true;

  const children = node.children || [];
  for (const childId of children) {
    markPrimaryLineage(tree, childId);
  }
}

export function relationshipToTreeTransformer(
  relationships: ILegalTransactionRelationship[],
  rootId: Id
): Tree {
  const tree: Record<Id, TreeNodeType> = {};

  initializeAllPersons(tree, relationships);

  tree[rootId].isRoot = true;

  assignMarriages(tree, relationships);
  assignFamilies(tree, relationships);

  markPrimaryLineage(tree, rootId);

  console.log('Transformed tree:', tree);

  return tree;
}
